import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Textarea from "@amzn/meridian/textarea";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import editTokens from "@amzn/meridian-tokens/base/icon/edit";
import Modal from "@amzn/meridian/modal";
import Loader from "@amzn/meridian/loader";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BatchJobDefinition, DataSource } from "src/services/automatedJobsService";
import {
  validateFilterClause,
  clearFilterValidation,
  getDataSources,
} from "src/store/automatedBatchJobSlice";
import { RootState, AppDispatch } from "src/store/store";
import { setSelectedModelId, getModels } from "src/store/modelManagementSlice";

export interface AutomatedJobFormData {
  name: string;
  modelId: string;
  status: string;
  source: DataSource;
  source_data_filter_clause: string; // Replaced dataSourceSelectClause
  destination: {
    tableName: string;
    id_column_name: string;
  };
}

interface AutomatedJobFormProps {
  job?: BatchJobDefinition;
  onSubmit?: (data: AutomatedJobFormData) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onViewInsights?: () => void;
  isLoading?: boolean;
  submitError?: string;
  mode?: "create" | "edit" | "view";
}

const AutomatedJobForm: React.FC<AutomatedJobFormProps> = ({
  job,
  onSubmit,
  onCancel,
  onEdit,
  onViewInsights,
  isLoading = false,
  submitError = "",
  mode = "create",
}) => {
  const [formData, setFormData] = useState<AutomatedJobFormData>({
    name: "",
    modelId: "",
    status: "Draft",
    source: {
      name: "",
      source_table_name: "",
      source_id_column_name: "",
      source_text_expression: "",
      timestamp_column_name: "",
    },
    source_data_filter_clause: "",
    destination: {
      tableName: "",
      id_column_name: "",
    },
  });

  const dispatch = useDispatch<AppDispatch>();
  const { filterValidationLoading, filterValidationResult, dataSources } = useSelector(
    (state: RootState) => state.automatedBatchJobSlice,
  );
  const { apiErrors } = useSelector((state: RootState) => state.errorBoundary);

  // Get validation error from errorBoundary where interceptor stores it
  const filterValidationError = apiErrors?.["/automated_job/validate_filter_clause"] || null;
  const { selectedModel, modelView } = useSelector((state: RootState) => state.modelSlice);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditingTableName, setIsEditingTableName] = useState(false);
  const [tempTableName, setTempTableName] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Load data from Redux store on component mount
  useEffect(() => {
    // Load models and data sources if not already loaded
    if (modelView.length === 0) {
      dispatch(getModels());
    }
    if (dataSources.length === 0) {
      dispatch(getDataSources());
    }
  }, [dispatch, modelView.length, dataSources.length]);

  useEffect(() => {
    if (job) {
      setFormData({
        name: job.name,
        modelId: job.modelId,
        status: job.status || "Draft",
        source: job.source,
        source_data_filter_clause: job.source_data_filter_clause,
        destination: job.destination,
      });

      // Set selected model ID to load model details
      if (job.modelId) {
        dispatch(setSelectedModelId(job.modelId));
      }
    } else if (mode === "create") {
      // Only reset form when explicitly in create mode and no job is provided
      setFormData({
        name: "",
        modelId: "",
        status: "Draft",
        source: {
          name: "",
          source_table_name: "",
          source_id_column_name: "",
          source_text_expression: "",
          timestamp_column_name: "",
        },
        source_data_filter_clause: "",
        destination: {
          tableName: "",
          id_column_name: "",
        },
      });
      // Clear any validation results when switching to create mode
      dispatch(clearFilterValidation());
    }
  }, [job, mode, dispatch]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Job name is required";
    }

    if (!formData.modelId) {
      newErrors.modelId = "Model selection is required";
    }

    if (!formData.source.name.trim()) {
      newErrors.sourceName = "Data source selection is required";
    }

    if (!formData.destination.tableName.trim()) {
      newErrors.destinationTable = "Destination table name is required";
    }

    if (!formData.destination.id_column_name.trim()) {
      newErrors.destinationIdColumn = "Destination ID column is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (!validateForm()) {
      return;
    }

    // First validate the filter clause
    setIsValidating(true);
    try {
      const result = await dispatch(
        validateFilterClause({
          dataSource: formData.source,
          filterClause: formData.source_data_filter_clause,
          jobId: job?.id,
        }),
      ).unwrap();

      setValidationResult(result);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Validation failed:", error);
      // Error will be shown through the existing error handling
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmSubmit = () => {
    setShowConfirmationModal(false);
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
    setValidationResult(null);
  };

  const handleInputChange = (field: string, value: string) => {
    // Special handling for job name - convert to lowercase and replace spaces with underscores
    if (field === "name") {
      value = value
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleModelSelect = (modelId: string) => {
    const selectedModelFromView = modelView.find((model) => model.id === modelId);
    if (selectedModelFromView) {
      // Convert model name to table format: first replace spaces with underscores, then remove non-alphanumeric
      const tableName = selectedModelFromView.name
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

      setFormData((prev) => ({
        ...prev,
        modelId: modelId,
        destination: {
          tableName: tableName,
          id_column_name: prev.source.source_id_column_name || "source_id", // Use source ID column or common default
        },
      }));

      // Set selected model ID to load model details (upper level useEffect will handle the loading)
      dispatch(setSelectedModelId(modelId));

      // Clear model error
      if (errors.modelId) {
        console.log(errors);
        setErrors((prev) => ({
          ...prev,
          modelId: "",
        }));
      }
    }
  };

  const handleDataSourceSelect = (sourceName: string) => {
    const selectedDataSource = dataSources.find((ds) => ds.name === sourceName);
    if (selectedDataSource) {
      setFormData((prev) => ({
        ...prev,
        source: selectedDataSource,
        destination: {
          ...prev.destination,
          id_column_name: selectedDataSource.source_id_column_name, // Update destination ID column to match source
        },
      }));
      // Clear validation result when data source changes
      dispatch(clearFilterValidation());
      // Clear error
      if (errors.sourceName) {
        setErrors((prev) => ({
          ...prev,
          sourceName: "",
        }));
      }
    }
  };

  const handleValidateFilterClause = () => {
    if (!formData.source.name || !formData.source_data_filter_clause.trim()) {
      return;
    }

    // Clear any previous validation errors
    if (errors.sourceDataFilterClause) {
      setErrors((prev) => ({
        ...prev,
        sourceDataFilterClause: "",
      }));
    }

    // Dispatch Redux action
    dispatch(
      validateFilterClause({
        dataSource: formData.source,
        filterClause: formData.source_data_filter_clause,
        jobId: job?.id, // Pass job ID for existing jobs to use generate_sql_query
      }),
    );
  };

  const statusOptions = [
    { value: "Draft", label: "Draft" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const modelOptions = modelView.map((model) => ({
    value: model.id,
    label: model.name,
  }));

  return (
    <form onSubmit={handleSubmit}>
      <Column spacing="300">
        <Row widths={["50%", "50%"]} spacing="200" alignmentVertical="center">
          <Row widths={["50%", "50%"]} spacing="200" alignmentVertical="center">
            <Heading level={2} type="h200">
              {mode === "view"
                ? "Automated Job Details"
                : job
                  ? "Edit Automated Job"
                  : "Create New Automated Job"}
            </Heading>
            <Row spacing="200" alignmentHorizontal="end">
              {mode === "view" ? (
                <>
                  {onViewInsights && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={onViewInsights}
                      disabled={isLoading}
                    >
                      View Insights
                    </Button>
                  )}
                  {onEdit && (
                    <Button type="secondary" size="small" onClick={onEdit} disabled={isLoading}>
                      Edit Job
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {onCancel && (
                    <Button type="secondary" size="small" onClick={onCancel} disabled={isLoading}>
                      Cancel
                    </Button>
                  )}
                  {onSubmit && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleSubmit}
                      disabled={
                        isLoading ||
                        isValidating ||
                        !formData.name.trim() ||
                        !formData.modelId ||
                        !formData.source.name
                      }
                    >
                      {isValidating ? (
                        <Row spacing="100" alignmentVertical="center">
                          <Loader size="small" />
                          <Text>Validating...</Text>
                        </Row>
                      ) : isLoading ? (
                        <Row spacing="100" alignmentVertical="center">
                          <Loader size="small" />
                          <Text>Saving...</Text>
                        </Row>
                      ) : job ? (
                        "Update"
                      ) : (
                        "Create"
                      )}
                    </Button>
                  )}
                </>
              )}
            </Row>
          </Row>
          <div></div>
        </Row>

        {mode === "view" && job ? (
          <Row widths={["50%", "50%"]} spacing="200">
            <div
              style={{
                backgroundColor: "#e7f3ff",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #b3d9ff",
                fontSize: "14px",
              }}
            >
              <Text type="b100">
                ℹ️ <strong>Job Status:</strong> {formData.status} | <strong>Owner:</strong>{" "}
                {job.ownerAlias} | <strong>Version:</strong> {job.version}
              </Text>
            </div>
            <div></div>
          </Row>
        ) : (
          !job && (
            <Row widths={["50%", "50%"]} spacing="200">
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ffeaa7",
                  fontSize: "14px",
                }}
              >
                <Text type="b100">
                  ℹ️ <strong>Draft Status:</strong> New jobs will be created in Draft status and
                  initially backfill up to 3,000 rows for testing. Please make sure sample data gets
                  generated correctly, then change to Active status for full backfill.
                </Text>
              </div>
              <div></div>
            </Row>
          )
        )}

        <Row widths={["15%", "35%", "fill"]} spacing="200" alignmentVertical="center">
          <Text type="b200">Job Name *</Text>
          <Column>
            <Input
              value={formData.name}
              onChange={(value) => handleInputChange("name", value)}
              placeholder="Enter job name"
              disabled={isLoading || !!job || mode === "view"}
            />
            {errors.name && <Text color="error">{errors.name}</Text>}
            {mode === "create" && (
              <Text type="b100" color="secondary">
                Job name will be automatically formatted to lowercase with underscores (spaces and
                special characters will be replaced)
              </Text>
            )}
          </Column>
          <div></div>
        </Row>

        {job && (
          <Row widths={["15%", "35%", "fill"]} spacing="200" alignmentVertical="center">
            <Text type="b200">Status</Text>
            <Column>
              <Select
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                disabled={isLoading || mode === "view"}
              >
                {statusOptions.map((option) => (
                  <SelectOption key={option.value} value={option.value} label={option.label} />
                ))}
              </Select>
              {errors.status && <Text color="error">{errors.status}</Text>}
            </Column>
            <div></div>
          </Row>
        )}

        <Row widths={["15%", "35%", "fill"]} spacing="200" alignmentVertical="top">
          <Text type="b200">Data Source *</Text>
          <Column>
            <Select
              value={formData.source.name}
              onChange={(value) => handleDataSourceSelect(value)}
              disabled={isLoading || !!job || mode === "view"}
            >
              <SelectOption value="" disabled label="Select data source" />
              {dataSources.map((dataSource) => (
                <SelectOption
                  key={dataSource.name}
                  value={dataSource.name}
                  label={dataSource.name}
                />
              ))}
            </Select>
            {errors.sourceName && <Text color="error">{errors.sourceName}</Text>}
          </Column>
          <div></div>
        </Row>

        {formData.source.name && (
          <Row widths={["50%", "50%"]} spacing="200" alignmentVertical="top">
            <Column>
              <Text type="b200">Data Source Details:</Text>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6",
                }}
              >
                <Text type="b200">
                  <strong>Table:</strong> {formData.source.source_table_name}
                  <br />
                  <strong>ID Column:</strong> {formData.source.source_id_column_name}
                  <br />
                  <strong>Text Expression:</strong> {formData.source.source_text_expression}
                  <br />
                  <strong>Timestamp Column:</strong> {formData.source.timestamp_column_name}
                </Text>
              </div>
            </Column>
            <div></div>
          </Row>
        )}

        <Row widths={["15%", "35%", "fill"]} spacing="200" alignmentVertical="top">
          <Text type="b200">Filter Clause *</Text>
          <Column>
            <Textarea
              value={formData.source_data_filter_clause}
              onChange={(value) => {
                handleInputChange("source_data_filter_clause", value);
                dispatch(clearFilterValidation());
              }}
              placeholder="e.g., finding_creation_date >= '2023-12-31 00:00:00'"
              rows={3}
              disabled={isLoading || !formData.source.name || mode === "view"}
            />
            {mode !== "view" && (
              <div style={{ marginTop: "8px" }}>
                <Button
                  type="tertiary"
                  size="small"
                  onClick={handleValidateFilterClause}
                  disabled={
                    !formData.source.name ||
                    !formData.source_data_filter_clause.trim() ||
                    filterValidationLoading === "pending"
                  }
                >
                  {filterValidationLoading === "pending" ? (
                    <Row spacing="100" alignmentVertical="center">
                      <Loader size="small" />
                      <Text>Validating...</Text>
                    </Row>
                  ) : (
                    "Validate"
                  )}
                </Button>
              </div>
            )}
            {filterValidationError && (
              <div style={{ marginTop: "4px" }}>
                <Text color="error">{filterValidationError}</Text>
              </div>
            )}
            {filterValidationResult && (
              <div style={{ marginTop: "4px" }}>
                <Text type="b100" color="success">
                  ✅ Will process ~{filterValidationResult.row_count.toLocaleString()} rows
                </Text>
              </div>
            )}
          </Column>
          <div></div>
        </Row>

        <Row widths={["15%", "35%", "fill"]} spacing="200" alignmentVertical="center">
          <Text type="b200">Model *</Text>
          <Column>
            <Select
              value={formData.modelId}
              onChange={(value) => handleModelSelect(value)}
              disabled={isLoading || !!job || mode === "view" || !formData.source.name}
            >
              <SelectOption
                value=""
                disabled
                label={!formData.source.name ? "Select data source first" : "Select a model"}
              />
              {modelOptions.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
            {errors.modelId && <Text color="error">{errors.modelId}</Text>}
            {!formData.source.name && mode === "create" && (
              <Text type="b100" color="secondary">
                Please select a data source first
              </Text>
            )}
          </Column>
          <div></div>
        </Row>

        {formData.modelId && formData.source.name && (
          <Row widths={["50%", "50%"]} spacing="200" alignmentVertical="top">
            <Column>
              <Text type="b200">Generated AI insight Table Schema:</Text>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6",
                }}
              >
                <Text type="b200">
                  <strong>Table Name:</strong>
                  {isEditingTableName ? (
                    <span style={{ marginLeft: "8px" }}>
                      <span style={{ display: "inline-block", width: "200px", marginRight: "8px" }}>
                        <Input
                          value={tempTableName}
                          onChange={(value) => setTempTableName(value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setFormData((prev) => ({
                                ...prev,
                                destination: {
                                  ...prev.destination,
                                  tableName: tempTableName
                                    .toLowerCase()
                                    .replace(/\s+/g, "_")
                                    .replace(/[^a-z0-9_]/g, ""),
                                },
                              }));
                              setIsEditingTableName(false);
                            } else if (e.key === "Escape") {
                              setIsEditingTableName(false);
                              setTempTableName(formData.destination.tableName);
                            }
                          }}
                        />
                      </span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            destination: {
                              ...prev.destination,
                              tableName: tempTableName
                                .toLowerCase()
                                .replace(/\s+/g, "_")
                                .replace(/[^a-z0-9_]/g, ""),
                            },
                          }));
                          setIsEditingTableName(false);
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        type="secondary"
                        size="small"
                        onClick={() => {
                          setIsEditingTableName(false);
                          setTempTableName(formData.destination.tableName);
                        }}
                      >
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <span>
                      {" " + formData.destination.tableName}
                      {mode === "create" && (
                        <span style={{ marginLeft: "16px" }}>
                          <Button
                            type="tertiary"
                            size="small"
                            onClick={() => {
                              setIsEditingTableName(true);
                              setTempTableName(formData.destination.tableName);
                            }}
                          >
                            <Icon tokens={editTokens} />
                            Edit
                          </Button>
                        </span>
                      )}
                    </span>
                  )}
                  <br />
                  <strong>Primary Key:</strong> {formData.destination.id_column_name}
                  <br />
                  <strong>Model generate insights fields:</strong>
                  <br />
                  {selectedModel &&
                    selectedModel.id === formData.modelId &&
                    selectedModel.fields &&
                    Object.values(selectedModel.fields).map((field: any, index) => (
                      <span key={field.id}>
                        • {field.name.toLowerCase().replace(/\s+/g, "_")}
                        <br />
                      </span>
                    ))}
                </Text>
              </div>
            </Column>
            <div></div>
          </Row>
        )}

        {submitError && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #f5c6cb",
            }}
          >
            <Text color="error">❌ {submitError}</Text>
          </div>
        )}
      </Column>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmationModal}
        onClose={handleCancelSubmit}
        title={job ? "Confirm Job Update" : "Confirm Job Creation"}
      >
        <Column spacing="400">
          <Text type="h300">{job ? "Update Automated Job" : "Create New Automated Job"}</Text>

          {validationResult && (
            <div
              style={{
                backgroundColor: "#e7f3ff",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #b3d9ff",
              }}
            >
              <Text type="b200">
                <strong>✅ Validation Successful</strong>
                <br />
                <strong>Rows to process:</strong> ~
                {validationResult.row_count?.toLocaleString() || 0} records
                <br />
                <strong>Data source:</strong> {formData.source.name}
                <br />
                <strong>Model:</strong> {modelView.find((m) => m.id === formData.modelId)?.name}
              </Text>
            </div>
          )}

          {!job && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #ffeaa7",
              }}
            >
              <Text type="b200">
                <strong>📋 New Table Will Be Created:</strong>
                <br />
                <strong>Table Name:</strong> {formData.destination.tableName}
                <br />
                <strong>Primary Key:</strong> {formData.destination.id_column_name}
                <br />
                <strong>Schema Fields:</strong>
                <br />• {formData.destination.id_column_name} - Primary key
                <br />
                {selectedModel &&
                  selectedModel.id === formData.modelId &&
                  selectedModel.fields &&
                  Object.values(selectedModel.fields).map((field: any, index) => (
                    <span key={field.id}>
                      • {field.name.toLowerCase().replace(/\s+/g, "_")}
                      <br />
                    </span>
                  ))}
              </Text>
            </div>
          )}

          {job && (
            <div
              style={{
                backgroundColor: "#e8f5e8",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #c3e6c3",
              }}
            >
              <Text type="b200">
                <strong>🔄 Existing Job Will Be Updated:</strong>
                <br />
                <strong>Job Name:</strong> {formData.name}
                <br />
                <strong>Status:</strong> {job.status} → {formData.status}
                <br />
                <strong>Table:</strong> {formData.destination.tableName} (existing)
                <br />
                {(() => {
                  const oldStatus = job.status;
                  const newStatus = formData.status;

                  if (oldStatus === newStatus) {
                    return (
                      <strong>
                        Configuration will be updated and new data will be backfilled based on the
                        updated filter clause.
                      </strong>
                    );
                  } else if (newStatus === "Active") {
                    return (
                      <strong>
                        Job will be activated and full backfill process will start with the updated
                        configuration.
                      </strong>
                    );
                  } else if (newStatus === "Inactive") {
                    return (
                      <strong>
                        Job will be deactivated. No new data processing will occur until
                        reactivated.
                      </strong>
                    );
                  } else if (newStatus === "Draft") {
                    return (
                      <strong>
                        Job will be moved to draft status for testing. Limited backfill (up to 3,000
                        rows) will be processed.
                      </strong>
                    );
                  } else {
                    return (
                      <strong>
                        New data will be backfilled based on the updated filter clause.
                      </strong>
                    );
                  }
                })()}
              </Text>
            </div>
          )}

          <Text>
            {job
              ? (() => {
                  const oldStatus = job.status;
                  const newStatus = formData.status;

                  if (oldStatus === newStatus) {
                    return "Are you sure you want to update this automated job? This will trigger a new backfill process with the updated configuration.";
                  } else if (oldStatus === "Draft" && newStatus === "Active") {
                    return "Are you sure you want to activate this job? This will start the full backfill process and begin regular data processing.";
                  } else if (oldStatus === "Active" && newStatus === "Inactive") {
                    return "Are you sure you want to deactivate this job? This will stop all data processing until the job is reactivated.";
                  } else if (oldStatus === "Inactive" && newStatus === "Draft") {
                    return "Are you sure you want to move this job to draft status? This will enable limited testing with up to 3,000 rows.";
                  } else if (oldStatus === "Inactive" && newStatus === "Active") {
                    return "Are you sure you want to reactivate this job? This will resume full data processing and backfill.";
                  } else if (oldStatus === "Active" && newStatus === "Draft") {
                    return "Are you sure you want to move this job to draft status? This will limit processing to 3,000 rows for testing purposes.";
                  } else if (oldStatus === "Draft" && newStatus === "Inactive") {
                    return "Are you sure you want to deactivate this job? This will stop all data processing.";
                  } else {
                    return "Are you sure you want to update this automated job? This will apply the new configuration and status.";
                  }
                })()
              : "Are you sure you want to create this automated job? This will create a new table and start the backfill process."}
          </Text>

          <Row spacing="200" alignmentHorizontal="end">
            <Button type="secondary" onClick={handleCancelSubmit}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleConfirmSubmit} disabled={isLoading}>
              {isLoading ? "Processing..." : job ? "Update Job" : "Create Job"}
            </Button>
          </Row>
        </Column>
      </Modal>
    </form>
  );
};

export default AutomatedJobForm;
